import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentsService } from '../services/api';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Input } from './ui/input';

const InvestigationPanel = ({ complaintId, clientSlug, canEdit = true, onRefresh }) => {
  const [phase, setPhase] = useState('none');
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [rejectionNotes, setRejectionNotes] = useState('');

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const response = await agentsService.getInvestigation(complaintId);
        if (cancelled) {
          return;
        }

        setPhase(response.phase || 'none');
        setPlan(response.plano_investigacao || null);
        setError('');
      } catch (fetchError) {
        if (cancelled) {
          return;
        }

        setError(fetchError.response?.data?.error || fetchError.message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [complaintId]);

  useEffect(() => {
    if (phase !== 'investigation_pending') {
      return undefined;
    }

    const interval = window.setInterval(async () => {
      try {
        const response = await agentsService.getInvestigation(complaintId);
        setPhase(response.phase || 'none');
        setPlan(response.plano_investigacao || null);

        if (response.phase === 'investigation_done') {
          toast.success('Plano de investigação gerado.');
          onRefresh?.();
        }
      } catch (pollError) {
        setError(pollError.response?.data?.error || pollError.message);
      }
    }, 2500);

    return () => window.clearInterval(interval);
  }, [phase, complaintId, onRefresh]);

  const handleStart = async () => {
    try {
      setStarting(true);
      setError('');
      await agentsService.triggerInvestigation(complaintId);
      setPhase('investigation_pending');
      setPlan(null);
      toast.info('Investigação iniciada.');
      onRefresh?.();
    } catch (startError) {
      setError(startError.response?.data?.error || startError.message);
      toast.error('Erro ao iniciar investigação.');
    } finally {
      setStarting(false);
    }
  };

  const handleApprove = async (approved) => {
    try {
      setApprovalLoading(true);
      await agentsService.approvePlan(
        complaintId,
        approved,
        approved ? null : rejectionNotes.trim() || 'Plano rejeitado pelo admin'
      );
      setPhase(approved ? 'approved' : 'investigation_done');
      toast.success(approved ? 'Plano aprovado.' : 'Plano rejeitado.');
      onRefresh?.();
    } catch (approveError) {
      setError(approveError.response?.data?.error || approveError.message);
      toast.error('Erro ao atualizar aprovação do plano.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const isReady = phase === 'investigation_done' && plan;

  return (
    <Card className="border-blue-200">
      <CardHeader className="bg-blue-50">
        <CardTitle>Investigação com IA</CardTitle>
        <p className="text-sm text-gray-600">
          Cliente: <span className="font-medium text-gray-800">{clientSlug || 'N/A'}</span>
        </p>
        {plan?.metadata?.triagem_automatica ? (
          <p className="text-xs text-indigo-600">
            Plano gerado automaticamente na triagem inicial (fila de aprovação).
          </p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <div className="text-sm text-gray-500">Carregando status da investigação...</div>
        ) : null}

        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium uppercase text-gray-700">
            {phase}
          </span>

          {phase === 'investigation_pending' ? (
            <span className="text-sm text-blue-700">Analisando denúncia e políticas internas...</span>
          ) : null}

          {canEdit && phase !== 'investigation_done' && phase !== 'approved' ? (
            <Button
              onClick={handleStart}
              disabled={starting || approvalLoading || phase === 'investigation_pending'}
            >
              Iniciar Investigação
            </Button>
          ) : null}

          {canEdit && (phase === 'investigation_done' || phase === 'approved') ? (
            <Button
              onClick={handleStart}
              disabled={starting || approvalLoading || phase === 'investigation_pending'}
              variant="outline"
            >
              Gerar Novo Plano
            </Button>
          ) : null}
        </div>

        {isReady ? (
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase text-gray-500">Título</p>
                <p className="text-sm font-medium text-gray-900">
                  {plan.denuncia_estruturada?.titulo || 'Sem título'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase text-gray-500">Natureza</p>
                <p className="text-sm font-medium text-gray-900">
                  {plan.denuncia_estruturada?.natureza || 'N/A'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase text-gray-500">Severidade</p>
                <p className="text-sm font-medium text-gray-900">
                  {plan.denuncia_estruturada?.severidade || 'N/A'}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs uppercase text-gray-500">Urgência</p>
                <p className="text-sm font-medium text-gray-900">
                  {plan.denuncia_estruturada?.urgencia || 'N/A'}
                </p>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4">
              <h4 className="mb-3 font-semibold text-gray-900">Perguntas Investigativas</h4>
              <ul className="space-y-2 text-sm text-gray-700">
                {(plan.plano_acao?.perguntas_investigativas || []).map((question, index) => (
                  <li key={`${question}-${index}`}>{index + 1}. {question}</li>
                ))}
              </ul>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-lg border border-gray-200 p-4">
                <h4 className="mb-3 font-semibold text-gray-900">Entrevistas Planejadas</h4>
                <div className="space-y-3 text-sm text-gray-700">
                  {(plan.plano_acao?.entrevistas_planejadas || []).map((interview, index) => (
                    <div key={`${interview.pessoa}-${index}`} className="rounded-md bg-gray-50 p-3">
                      <p className="font-medium text-gray-900">{interview.pessoa}</p>
                      <p>{interview.objetivo}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 p-4">
                <h4 className="mb-3 font-semibold text-gray-900">Documentos Necessários</h4>
                <ul className="space-y-2 text-sm text-gray-700">
                  {(plan.plano_acao?.documentos_necessarios || []).map((document, index) => (
                    <li key={`${document}-${index}`}>{index + 1}. {document}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
              <p>
                <span className="font-medium">Notas do Investigador:</span>{' '}
                {plan.plano_acao?.notas_investigador || 'Sem notas adicionais.'}
              </p>
              <p className="mt-2 text-xs text-gray-500">
                Tokens: {plan.metadata?.tokens_used || 0} | Custo: $
                {(plan.metadata?.cost_usd || 0).toFixed(6)}
              </p>
            </div>

            {canEdit ? (
              <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                <Input
                  value={rejectionNotes}
                  onChange={(event) => setRejectionNotes(event.target.value)}
                  placeholder="Observações para rejeitar ou pedir um novo plano"
                />
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleApprove(true)}
                    disabled={approvalLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Aprovar Plano
                  </Button>
                  <Button
                    onClick={() => handleApprove(false)}
                    disabled={approvalLoading}
                    variant="outline"
                  >
                    Rejeitar Plano
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {phase === 'approved' ? (
          <Alert variant="success">
            <AlertDescription>Plano aprovado. O próximo passo é registrar entrevistas e gerar o relatório.</AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default InvestigationPanel;
